import { buildTrustDeck, resolveTrustLabels, resolveTrustRhythm } from './resolve-trust.js';
import { contrastFor, isHexColor } from './color.js';
import { defaultAccent } from './constants.js';
import { resolveAnnouncementKindLabels, resolveAnnouncements, resolveLabel, resolveNudgeOptions } from './resolve-nudges.js';
import { supportedLanguages } from './runtime.js';

/**
 * Normalizes the raw config payload into safe rendering options.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{accent: string, contrast: string, accentDark: string, contrastDark: string, tint: string|null,
 *            side: string, theme: string, nudges: Array, nudgeDelayMs: number, nudgeEveryPage: boolean,
 *            nudgeCloseLabel: string}}
 */
export function resolveOptions(config) {
    const accentValid = config && isHexColor(config.accentColor);
    const accent = accentValid ? config.accentColor : defaultAccent;
    // The dark look has its own accent; falling back to the light one is what a widget without a dark
    // pick already gets from the server, so this only catches a payload that predates the field.
    const accentDarkValid = config && isHexColor(config.accentColorDark);
    const accentDark = accentDarkValid ? config.accentColorDark : accent;

    return {
        accent: accent,
        contrast: accentValid && isHexColor(config.accentContrastColor)
            ? config.accentContrastColor
            : contrastFor(accent),
        accentDark: accentDark,
        contrastDark: accentDarkValid && isHexColor(config.accentContrastColorDark)
            ? config.accentContrastColorDark
            : contrastFor(accentDark),
        // The soft variant of the accent, derived server-side (WidgetAccentPalette). Null without a
        // custom accent, and the panel outline then keeps its neutral default.
        tint: config && isHexColor(config.accentTintColor) ? config.accentTintColor : null,
        side: config && config.position === 'left' ? 'left' : 'right',
        // The mark on the closed launcher. Anything this build does not know falls back to the sparkles,
        // an older payload without the field included — the launcher must survive a config it cannot read.
        launcherIcon: config && config.launcherIcon === 'custom' ? 'custom' : 'sparkles',
        // Only ever an absolute https URL, and only for the choice it belongs to. It goes into an <img>
        // src on somebody else's page: the server already resolved and gated it, this is the same
        // defense in depth the accent hex gets.
        launcherIconUrl: config && typeof config.launcherIconUrl === 'string'
            && config.launcherIconUrl.indexOf('https://') === 0 && config.launcherIconUrl.length <= 2000
            ? config.launcherIconUrl
            : null,
        // The far end of the launcher gradient, one per look — already resolved and already gated by the
        // server (WidgetLauncherPaint). Null is the flat accent, which is what every widget without the
        // switch gets and what an older payload leaves behind.
        gradient: config && isHexColor(config.launcherGradientColor) ? config.launcherGradientColor : null,
        gradientDark: config && isHexColor(config.launcherGradientColorDark)
            ? config.launcherGradientColorDark
            : null,
        // The widget's light/dark setting; the nudge stylesheet resolves 'auto' via prefers-color-scheme.
        theme: config && (config.theme === 'dark' || config.theme === 'light' || config.theme === 'api')
            ? config.theme
            : 'auto',
        // The widget's standard language, or '' on an older payload — the server then resolves the
        // interface language on its own. Beside it the content languages the page's declaration and the
        // host's switch may pick from; every app language where the payload names none, so an older
        // server keeps the behaviour it had (see effectiveLanguage).
        language: config && supportedLanguages.indexOf(config.language) >= 0 ? config.language : '',
        languages: resolveContentLanguages(config),
        // Whatever the contractor ticked, as one deck: the forms arriving together are one rotation
        // rather than six surfaces. Null where nothing was ticked — the corner then stays empty.
        trustCards: buildTrustDeck(config),
        trustLabels: resolveTrustLabels(config),
        ...resolveTrustRhythm(config),
        announcements: resolveAnnouncements(config),
        // Empty when the server sent none (older deployment, or no campaign travelling): the card then
        // renders without an expand control and simply stays unclamped — see showAnnouncementCard.
        announcementExpandLabel: resolveLabel(config, 'announcementExpandLabel', ''),
        announcementCollapseLabel: resolveLabel(config, 'announcementCollapseLabel', ''),
        // Null when the server sent none (older deployment, or no minimal campaign travelling): the
        // minimal layout then simply renders without its kind eyebrow — see appendAnnouncementContent.
        announcementKindLabels: resolveAnnouncementKindLabels(config),
        ...resolveNudgeOptions(config)
    };
}

/**
 * The content languages of the widget, validated against the app languages. Every app language where
 * the payload carries none (or nothing usable), so a loader ahead of its server changes nothing.
 * @param {object|null} config - The config payload
 * @returns {string[]}
 */
function resolveContentLanguages(config) {
    const listed = config && Array.isArray(config.languages)
        ? config.languages.filter((language) => supportedLanguages.indexOf(language) >= 0)
        : [];

    return listed.length > 0 ? listed : supportedLanguages;
}
